import { useEffect, useRef, useState } from 'react';
import { FaceState } from '../../pal/face';
import type { TripSnapshot } from '../../drive/trip-session';

export type DriverStatus =
    | 'idle'
    | 'requesting'
    | 'connecting'
    | 'initialising'
    | 'connected'
    | 'disconnected'
    | 'error';

export const SPEED_EXCITED_ENTER_KMH = 105;
export const SPEED_EXCITED_EXIT_KMH = 95;
export const SPEED_SUPER_ENTER_KMH = 130;
export const SPEED_SUPER_EXIT_KMH = 120;
export const RPM_INTENSE_ENTER = 4700;
export const RPM_INTENSE_EXIT = 4200;
export const SCARED_DURATION_MS = 3000;
export const MIN_AMBIENT_MS = 875;
const MOVING_KMH = 1.5;

export interface HysteresisState {
    readonly intense: boolean;
    readonly excited: boolean;
    readonly superExcited: boolean;
}

export const INITIAL_HYSTERESIS: HysteresisState = {
    intense: false,
    excited: false,
    superExcited: false,
};

export function computeAmbientFaceState(
    snapshot: TripSnapshot,
    status: DriverStatus,
    prev: HysteresisState,
): { state: FaceState; hysteresis: HysteresisState } {
    if (status === 'disconnected' || status === 'error') {
        return { state: FaceState.DISCONNECTED, hysteresis: INITIAL_HYSTERESIS };
    }

    const activeTripState = snapshot.state === 'driving' || snapshot.state === 'connected';
    if (!activeTripState) {
        return { state: FaceState.IDLE, hysteresis: INITIAL_HYSTERESIS };
    }

    const rpm = snapshot.last_rpm ?? 0;
    const speed = snapshot.last_speed_kmh ?? 0;

    const intense = prev.intense ? rpm >= RPM_INTENSE_EXIT : rpm >= RPM_INTENSE_ENTER;
    const superExcited = prev.superExcited ? speed >= SPEED_SUPER_EXIT_KMH : speed >= SPEED_SUPER_ENTER_KMH;
    const excited = prev.excited ? speed >= SPEED_EXCITED_EXIT_KMH : speed >= SPEED_EXCITED_ENTER_KMH;

    const hysteresis: HysteresisState = { intense, superExcited, excited };

    let state: FaceState;
    if (intense) state = FaceState.INTENSE;
    else if (superExcited) state = FaceState.SUPER_EXCITED;
    else if (excited) state = FaceState.EXCITED;
    else if (snapshot.state === 'driving' && speed >= MOVING_KMH) state = FaceState.CRUISING;
    else state = FaceState.IDLE;

    return { state, hysteresis };
}

export function useDriveFaceState(snapshot: TripSnapshot, status: DriverStatus): FaceState {
    const [faceState, setFaceState] = useState<FaceState>(FaceState.IDLE);

    const prevBrakesRef = useRef(snapshot.hard_brakes);
    const scaredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isScaredRef = useRef(false);
    const hysteresisRef = useRef<HysteresisState>(INITIAL_HYSTERESIS);
    const currentAmbientRef = useRef<FaceState>(FaceState.IDLE);
    const lastAmbientChangeMs = useRef(0);

    useEffect(() => {
        return () => {
            if (scaredTimerRef.current !== null) {
                clearTimeout(scaredTimerRef.current);
                scaredTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (snapshot.state !== 'idle') return;
        prevBrakesRef.current = 0;
        hysteresisRef.current = INITIAL_HYSTERESIS;
        currentAmbientRef.current = FaceState.IDLE;
        isScaredRef.current = false;
        lastAmbientChangeMs.current = 0;
        if (scaredTimerRef.current !== null) {
            clearTimeout(scaredTimerRef.current);
            scaredTimerRef.current = null;
        }
        setFaceState(FaceState.IDLE);
    }, [snapshot.state]);

    useEffect(() => {
        if (snapshot.hard_brakes <= prevBrakesRef.current) return;
        prevBrakesRef.current = snapshot.hard_brakes;

        isScaredRef.current = true;
        setFaceState(FaceState.SCARED);

        if (scaredTimerRef.current !== null) clearTimeout(scaredTimerRef.current);
        scaredTimerRef.current = setTimeout(() => {
            isScaredRef.current = false;
            scaredTimerRef.current = null;
            setFaceState(currentAmbientRef.current);
        }, SCARED_DURATION_MS);
    }, [snapshot.hard_brakes]);

    useEffect(() => {
        const { state: next, hysteresis: nextHysteresis } = computeAmbientFaceState(
            snapshot,
            status,
            hysteresisRef.current,
        );

        if (next === currentAmbientRef.current) return;

        const now = Date.now();
        if (now - lastAmbientChangeMs.current < MIN_AMBIENT_MS) return;

        hysteresisRef.current = nextHysteresis;
        currentAmbientRef.current = next;
        lastAmbientChangeMs.current = now;

        if (!isScaredRef.current) {
            setFaceState(next);
        }
    }, [snapshot, status]);

    return faceState;
}
